import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import { LAZY_LOAD, IMAGE_OPTIMIZATION } from '../domain/constants';

export interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src?: string;
    alt?: string;
    className?: string;
    containerClassName?: string;
    fetchPriority?: 'high' | 'low' | 'auto';
    threshold?: number;
    rootMargin?: string;
}

const LazyImageComponent = ({ 
    src, 
    alt, 
    className, 
    containerClassName, 
    threshold = LAZY_LOAD.INTERSECTION_THRESHOLD, 
    rootMargin = LAZY_LOAD.ROOT_MARGIN,
    ...props 
}: LazyImageProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setIsLoaded(false);
        setHasError(false);
        if (!src) return;
        if (!('IntersectionObserver' in window)) {
            setIsVisible(true);
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin, threshold }
        );
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        return () => observer.disconnect();
    }, [src, rootMargin, threshold]);

    const getOptimizedSrc = useMemo(() => (originalSrc?: string) => {
        if (!originalSrc) return '';
        if (originalSrc.includes('images.unsplash.com')) {
            const hasParams = originalSrc.includes('?');
            const separator = hasParams ? '&' : '?';
            if (!originalSrc.includes('w=')) return `${originalSrc}${separator}${IMAGE_OPTIMIZATION.getParams()}`;
        }
        return originalSrc;
    }, []);

    const finalSrc = useMemo(() => getOptimizedSrc(src), [getOptimizedSrc, src]);

    const handleLoad = useCallback(() => setIsLoaded(true), []);

    return (
        <div ref={containerRef} className={`relative overflow-hidden bg-gray-100 dark:bg-white/5 flex items-center justify-center ${containerClassName || ''}`}>
            {(!isLoaded || !isVisible) && !hasError && (
                <div className="absolute inset-0 bg-gray-200 dark:bg-white/10 animate-pulse z-10" />
            )}
            {hasError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 dark:bg-surface-dark text-gray-400 z-20">
                    <span className="material-symbols-outlined text-3xl opacity-50">broken_image</span>
                </div>
            )}
            {isVisible && finalSrc ? (
                <img
                    src={finalSrc}
                    alt={alt}
                    className={`${className || ''} transition-opacity duration-500 ease-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                    onLoad={handleLoad}
                    onError={() => setHasError(true)}
                    decoding="async"
                    loading="lazy"
                    {...props}
                />
            ) : isVisible && !finalSrc ? (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-white/5">
                    <span className="material-symbols-outlined text-gray-300">image_not_supported</span>
                </div>
            ) : null}
        </div>
    );
};

LazyImageComponent.displayName = 'LazyImage';

export const LazyImage = memo(LazyImageComponent);